/**
 * Medição de espessura da pintura por peça (F2.3, laudo da estética) — fonte
 * única do vocabulário. Sem dependência nenhuma: `@mustauto/utils` importa
 * pelo subpath `@mustauto/types/paint-readings` (ver "O deploy pode falhar no
 * empacotamento", CLAUDE.md).
 *
 * Polir sem medir é como se queima verniz: o polimento tira mícrons, e a peça
 * que chegou com 80 µm (repintada, ou já polida demais) não aguenta um corte.
 * O laudo de ENTRADA registra como o carro chegou — é o escudo jurídico do
 * detailer, como os 6 ângulos do checklist são o do mecânico. O de SAÍDA mostra
 * quanto o serviço tirou.
 */
import type { VehiclePanel } from './vehicle-panels';

/** Os dois momentos do laudo. */
export const PAINT_READING_PHASES = ['entry', 'exit'] as const;
export type PaintReadingPhase = (typeof PAINT_READING_PHASES)[number];

export const PAINT_READING_PHASE_LABELS: Record<PaintReadingPhase, string> = {
  entry: 'Entrada',
  exit: 'Saída',
};

/**
 * As peças que se medem, na ordem em que o detailer anda em volta do carro:
 * as peças pintadas de lataria e os para-choques (plástico mede com medidor
 * ultrassônico). Farol, lanterna, longarina e assoalho não entram — não há
 * pintura de acabamento para medir.
 */
export const PAINT_MEASURABLE_PANELS = [
  'capo',
  'para_choque_dianteiro',
  'para_lama_de',
  'porta_de',
  'porta_te',
  'lateral_te',
  'tampa_traseira',
  'para_choque_traseiro',
  'lateral_td',
  'porta_td',
  'porta_dd',
  'para_lama_dd',
  'teto',
] as const satisfies readonly VehiclePanel[];

export type PaintMeasurablePanel = (typeof PAINT_MEASURABLE_PANELS)[number];

export function isPaintMeasurablePanel(value: unknown): value is PaintMeasurablePanel {
  return typeof value === 'string' && (PAINT_MEASURABLE_PANELS as readonly string[]).includes(value);
}

/** Leituras por peça, em mícrons: `{ capo: [112, 98, 104] }`. */
export type PanelReadings = Partial<Record<PaintMeasurablePanel, number[]>>;

/** O laudo gravado na OS: entrada e saída, cada uma com quando foi medida. */
export interface PaintReadings {
  entry?: PanelReadings;
  entryAt?: string | null;
  exit?: PanelReadings;
  exitAt?: string | null;
}

/**
 * Abaixo disto a pintura está FINA: alerta na tela. Fixo por decisão do dono
 * do produto (24/09) — a referência de mercado para verniz de fábrica é
 * 100–160 µm; abaixo de ~90 um corte de polimento pode atravessar o verniz.
 */
export const PAINT_THIN_THRESHOLD_UM = 90;

/** Pontos por peça — o detailer mede 3 a 5 (centro e cantos). */
export const PAINT_READINGS_MAX_PER_PANEL = 5;

/** Faixa aceita de uma leitura: 1 µm a 2.000 µm (repintura grossa + PPF passam de 500). */
export const PAINT_READING_MIN_UM = 1;
export const PAINT_READING_MAX_UM = 2000;
